import { useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { UserX, Shield, User, Loader2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import { teamsAPI } from "@/lib/api";
import { useToast } from "@/hooks/use-toast";
import { ConfirmationModal } from "./ConfirmationModal";
import { getLocalizedErrorMessage } from "@/lib/error-i18n";

interface TeamMember {
  _id?: string;
  id?: string;
  userId?: {
    _id: string;
    name: string;
    email: string;
  } | string;
  role: "member" | "admin";
  status?: "INVITED" | "JOINED" | "REPORT_UPLOADED";
  joinedAt?: string;
}

interface TeamMembersListProps {
  teamId: string;
  teamName: string;
  currentUserId: string;
  members: TeamMember[];
  onMemberRemoved: () => void;
}

const getMemberId = (member: TeamMember): string => {
  if (typeof member.userId === 'object' && member.userId?._id) {
    return member.userId._id;
  }
  return member._id || member.id || "";
};

const getMemberName = (member: TeamMember): string => {
  if (typeof member.userId === 'object' && member.userId?.name) {
    return member.userId.name;
  }
  return "";
};

const getMemberEmail = (member: TeamMember): string => {
  if (typeof member.userId === 'object' && member.userId?.email) {
    return member.userId.email;
  }
  return "";
};

const getStatusConfig = (status: "INVITED" | "JOINED" | "REPORT_UPLOADED") => {
  switch (status) {
    case "INVITED":
      return {
        variant: "outline" as const,
        className: "text-xs border-yellow-500/50 text-yellow-600 bg-yellow-500/10",
        label: "Invited",
      };
    case "JOINED":
      return {
        variant: "outline" as const,
        className: "text-xs border-blue-500/50 text-blue-600 bg-blue-500/10",
        label: "Joined",
      };
    case "REPORT_UPLOADED":
      return {
        variant: "outline" as const,
        className: "text-xs border-green-500/50 text-green-600 bg-green-500/10",
        label: "Active",
      };
    default:
      return {
        variant: "outline" as const,
        className: "text-xs",
        label: status,
      };
  }
};

export const TeamMembersList = ({
  teamId,
  teamName,
  currentUserId,
  members,
  onMemberRemoved
}: TeamMembersListProps) => {
  const [deletingMemberId, setDeletingMemberId] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<{ open: boolean; member: TeamMember | null }>({
    open: false,
    member: null,
  });
  const { t, i18n } = useTranslation();
  const isRtl = i18n.language === "ar";
  const { toast } = useToast();

  const handleDeleteMember = async () => {
    if (!confirmDelete.member) return;

    const memberId = getMemberId(confirmDelete.member);
    setDeletingMemberId(memberId);

    try {
      const response = await teamsAPI.removeMember(teamId, memberId);
      if (response.success) {
        toast({
          title: t("teamMembers.memberRemoved"),
          description: t("teamMembers.memberRemovedDesc", { name: getMemberName(confirmDelete.member) }),
        });
        onMemberRemoved();
      } else {
        throw new Error(response.message);
      }
    } catch (error: any) {
      toast({
        title: t("common.error"),
        description: getLocalizedErrorMessage(error, "errors.requestFailed"),
        variant: "destructive",
      });
    } finally {
      setDeletingMemberId(null);
      setConfirmDelete({ open: false, member: null });
    }
  };

  return (
    <>
      <Card className="p-4 bg-card/50 backdrop-blur-sm border-border/50 text-start sm:p-6" dir={isRtl ? "rtl" : "ltr"}>
        <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
          <h3 className="text-lg font-bold sm:text-xl">{t("teamMembers.title")}</h3>
          <Badge variant="secondary">
            {members.length} {members.length === 1 ? t("teams.member") : t("teams.members")}
          </Badge>
        </div>

        <ScrollArea className="h-[400px] pe-4">
          <div className="space-y-3">
            {members.length === 0 ? (
              <p className="text-center text-muted-foreground py-8">
                {t("teamMembers.noMembers")}
              </p>
            ) : (
              members.map((member) => {
                const memberId = getMemberId(member);
                const memberName = getMemberName(member);
                const memberEmail = getMemberEmail(member);
                const isDeleting = deletingMemberId === memberId;
                const isCurrentUser = memberId === currentUserId;
                const status = member.status || "JOINED";
                const statusConfig = getStatusConfig(status);

                return (
                  <div
                    key={memberId || member._id}
                    className="flex flex-col gap-3 p-3 rounded-lg bg-background/50 border border-border/50 hover:bg-background/80 transition-colors sm:flex-row sm:items-center sm:justify-between"
                  >
                    <div className="flex min-w-0 flex-1 items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center">
                        {member.role === "admin" ? (
                          <Shield className="w-5 h-5 text-primary" />
                        ) : (
                          <User className="w-5 h-5 text-muted-foreground" />
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex flex-wrap items-center gap-2">
                          <p className="font-semibold truncate">{memberName}</p>

                          {/* STATUS BADGE */}
                          <Badge
                            variant={statusConfig.variant}
                            className={statusConfig.className}
                          >
                            {statusConfig.label}
                          </Badge>

                          {isCurrentUser && (
                            <Badge variant="outline" className="text-xs">
                              {t("teamMembers.you")}
                            </Badge>
                          )}
                        </div>
                        <p className="text-sm text-muted-foreground truncate">{memberEmail}</p>
                      </div>
                      <Badge variant={member.role === "admin" ? "default" : "secondary"} className="shrink-0">
                        {member.role === "admin" ? t("addMember.roleAdmin") : t("addMember.roleMember")}
                      </Badge>
                    </div>

                    {!isCurrentUser && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setConfirmDelete({ open: true, member })}
                        disabled={isDeleting}
                        className="self-end text-destructive hover:text-destructive hover:bg-destructive/10 sm:ms-2 sm:self-auto"
                      >
                        {isDeleting ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <UserX className="w-4 h-4" />
                        )}
                      </Button>
                    )}
                  </div>
                );
              })
            )}
          </div>
        </ScrollArea>
      </Card>

      <ConfirmationModal
        open={confirmDelete.open}
        onOpenChange={(open) => setConfirmDelete({ open, member: null })}
        onConfirm={handleDeleteMember}
        title={t("teamMembers.removeMemberTitle")}
        description={t("teamMembers.removeMemberDesc", { name: getMemberName(confirmDelete.member || {} as TeamMember) })}
        confirmText={t("teamMembers.removeButton")}
      />
    </>
  );
};
